import {
  TYPE,
  useChargeServiceRpt,
  useChargeServiceRptExport,
} from '@services/report'
import Flex from '@shared/Flex'
import { Button } from '@shared/Form'
import useSearch from '@shared/innmaxLib/hooks/useSearch'
import { rules } from '@shared/innmaxLib/utils/form'
import Layout from '@shared/Layout'
import Table from '@shared/Table'
import { downloadFile, toThousandSeparator } from '@shared/utils/webHelper'
import { Form } from 'antd'
import { ColumnsType } from 'antd/es/table'
import moment from 'moment'
import { isNil } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FormItem } from 'src/components/Form'
import OptionsSelect from 'src/components/OptionsSelect'
import { StatusBox } from '../style'

const initialReportType = {
  reportType: TYPE.YEAR,
  conditionDate: new Date().getFullYear(),
  createStartAt: moment().startOf('year').valueOf(),
  createEndAt: moment().endOf('year').valueOf(),
  pageSize: 999999999,
}

function ChargeService() {
  const { t } = useTranslation()

  const [form] = Form.useForm()

  const [currentType, setCurrentType] = React.useState(TYPE.YEAR)
  const [isSearch, setIsSearch] = React.useState(false)

  const { query, handleSearch, handleTableChange } = useSearch({
    form,
    initial: initialReportType,
    dateRangeField: ['startAt', 'endAt'],
  })

  const { dataSource, loading } = useChargeServiceRpt(query)

  const handleReportType = (val: any) => {
    setCurrentType(val?.reportType)
    return val
  }

  const [exporting, handleExport] = useChargeServiceRptExport(query, {
    onSuccess: (blob: any) => {
      downloadFile({
        blob,
        filename: 'ChargeReport',
      })
    },
  })

  const columns: ColumnsType<any> = [
    {
      title: '縣市',
      dataIndex: 'cityName',
      align: 'center',
      width: 150,
    },
    {
      title: '行政區',
      dataIndex: 'locationNs',
      align: 'center',
      width: 150,
    },
    {
      title: '收益排名',
      dataIndex: 'rank',
      align: 'center',
      sorter: Table.Sorter({
        sortField: 'rank',
      }),
    },
    {
      title: '站點',
      width: 300,
      dataIndex: 'sessionName',
      align: 'center',
    },
    {
      title: '充電費合計',
      dataIndex: 'chargeFee',
      align: 'center',
      render: v => toThousandSeparator(v),
      sorter: Table.Sorter({
        sortField: 'chargeFee',
      }),
    },
    {
      title: '快充金額',
      dataIndex: 'dcChargingFee',
      align: 'center',
      render: v => toThousandSeparator(v),
    },
    {
      title: '慢充金額',
      dataIndex: 'acChargingFee',
      align: 'center',
      render: v => toThousandSeparator(v),
    },
    {
      title: '廢單金額',
      dataIndex: 'cancelFee',
      align: 'center',
      render: v => toThousandSeparator(v),
    },
    {
      title: '充電樁數',
      dataIndex: 'chargePointCount',
      align: 'center',
      sorter: Table.Sorter({
        sortField: 'chargePointCount',
      }),
      render: v => toThousandSeparator(v) + '格',
    },
    {
      title: '快充數',
      dataIndex: 'dcChargingPointCount',
      align: 'center',
      render: v => toThousandSeparator(v),
    },
    {
      title: '慢充數',
      dataIndex: 'acChargingPointCount',
      align: 'center',
      render: v => toThousandSeparator(v),
    },
    {
      title: '開單數',
      dataIndex: 'orderCount',
      align: 'center',
      render: v => toThousandSeparator(v),
    },
    {
      title: '廢單數',
      dataIndex: 'cancelCount',
      align: 'center',
      render: v => toThousandSeparator(v),
    },
    {
      title: '總充電時數',
      dataIndex: 'chargeTime',
      align: 'center',
      render: v => toThousandSeparator(v),
    },

    {
      title: '用電量',
      dataIndex: 'chargeDegree',
      align: 'center',
      sorter: Table.Sorter({
        sortField: 'chargeDegree',
      }),
      render: v => toThousandSeparator(v) + 'kwh',
    },

    {
      title: '快充充電時數',
      dataIndex: 'dcChargingTime',
      align: 'center',
      render: v => toThousandSeparator(v) + 'h',
    },
    {
      title: '慢充充電時數',
      dataIndex: 'acChargingTime',
      align: 'center',
      render: v => toThousandSeparator(v) + 'h',
    },
    {
      title: '使用率',
      dataIndex: 'usageRate',
      align: 'center',
      render: v => toThousandSeparator(v) + '%',
    },
    {
      title: '週轉率',
      dataIndex: 'turnoverRate',
      align: 'center',
      render: v => toThousandSeparator(v) + '%',
    },
    {
      title: '故障率',
      dataIndex: 'failureRate',
      align: 'center',
      render: v => toThousandSeparator(v) + '%',
    },
    {
      title: '妥善率',
      dataIndex: 'properRate',
      align: 'center',
      render: v => toThousandSeparator(v) + '%',
    },
  ]

  const renderStatusContent = () => {
    const data: any = dataSource
    return (
      <div
        className="flex "
        style={{
          width: 'auto',
          padding: '0 10px',
          maxWidth: '85dvw',
          overflowX: 'auto',
          overflowY: 'hidden',
        }}>
        <StatusBox
          title="充電樁總數"
          data={data}
          dataKey="chargePointCount"
          detail={[
            {
              title: '快充',
              dataKey: 'dcChargingPointCount',
            },
            {
              title: '慢充',
              dataKey: 'acChargingPointCount',
            },
          ]}
        />

        <StatusBox
          title="充電費合計"
          data={data}
          unit="NTD"
          dataKey="chargeFee"
          detail={[
            {
              title: '快充',
              dataKey: 'dcChargingFee',
            },
            {
              title: '慢充',
              dataKey: 'acChargingFee',
            },
          ]}
        />
        <StatusBox
          title="停車費合計"
          unit="NTD"
          data={data}
          dataKey="parkingFee"
        />
        <StatusBox
          title="廢單金額"
          unit="NTD"
          data={data}
          dataKey="cancelFee"
        />

        <StatusBox title="開單數" unit="張" data={data} dataKey="orderCount" />

        <StatusBox title="廢單數" unit="張" data={data} dataKey="cancelCount" />

        <StatusBox
          title="總充電時數"
          data={data}
          unit="時"
          dataKey="chargeTime"
          detail={[
            {
              title: '快充',
              dataKey: 'dcChargingTime',
            },
            {
              title: '慢充',
              dataKey: 'acChargingTime',
            },
          ]}
        />

        <StatusBox
          title="用電量合計"
          unit="kwh"
          data={data}
          dataKey="chargeDegree"
        />

        <StatusBox title="使用率" unit="%" data={data} dataKey="usageRate" />
        <StatusBox title="週轉率" unit="%" data={data} dataKey="turnoverRate" />
        <StatusBox title="故障率" unit="%" data={data} dataKey="failureRate" />
        <StatusBox title="妥善率" unit="%" data={data} dataKey="properRate" />
      </div>
    )
  }

  React.useEffect(() => {
    if (!isNil(query) && !isNil(query.reportType) && !isNil(query.sessionId)) {
      setIsSearch(true)
    }
  }, [query])

  return (
    <>
      <Layout>
        <Form layout="inline" form={form}>
          <FormItem
            label={'縣市/行政區'}
            name="sessionId"
            rules={[rules.required]}
            className="w-[280px]">
            <OptionsSelect.CityAreaSelect
              className="w-130"
              supportAll={false}
            />
          </FormItem>
          <FormItem
            label={`${t('report:report type')} / ${t('report:time')}`}
            name="reportTime"
            initialValue={initialReportType}
            className={currentType === TYPE.DAY ? 'w-[380px]' : 'w-[250px]'}
            getValueFromEvent={handleReportType}>
            <OptionsSelect.ReportRangeTimeSelect />
          </FormItem>

          <FormItem.Button>
            <Button
              loading={false}
              type="primary"
              className="w-96"
              onClick={handleSearch}>
              {t('common:search')}
            </Button>
          </FormItem.Button>
          <FormItem.Button>
            <Button
              loading={exporting}
              type="primary"
              ghost
              className="w-96"
              onClick={handleExport}>
              {t('report:export')}
            </Button>
          </FormItem.Button>
        </Form>

        {renderStatusContent()}
        {isSearch ? (
          <>
            <div className="mt-44 mb-16">
              <Flex between>
                <Table.RowCount count={dataSource?.content?.length || 0} />
              </Flex>
            </div>
            <Table
              simpleMode
              columns={columns}
              loading={loading}
              dataSource={dataSource || []}
              onChange={handleTableChange}
              pagination={false}
              style={{
                width: '100%',
              }}
              scroll={{ x: 'max-content' }}
            />
          </>
        ) : (
          <div
            className="flex justify-center items-center text-center text-lg"
            style={{ color: '#8E8E8E', height: '50dvh', width: '50dvw' }}>
            <span>請先輸入搜尋條件範圍</span>
          </div>
        )}
      </Layout>
    </>
  )
}

export default React.memo(ChargeService)
